import { useStore } from '@nanostores/react';

import Icon from '@klaviyo-mkt/gatsby-components/atoms/icon';
import { generateClassName } from '@klaviyo-mkt/gatsby-components/shared/utils/functions';

import { RegisterButton } from '@academy/components/shared/register-button';
import { useGetCurriculumList } from '@academy/components/hooks/useGetCurriculumList';
import { $loggedIn } from '@academy/skilljar/stores/user';
import { getCourseMetaContent } from './helpers/getCourseMetaContent';

import * as styles from './styles/courseMeta.module.scss';

import type { ICourse, ICurriculumList, ILesson } from '../../graphql/types';

interface ICourseMeta {
  className?: string;
  course: ICourse;
  curriculumList: ICurriculumList;
  isUserLoggedIn: boolean;
  lessons: ILesson[];
  path: string;
  registrationUrl?: string | null;
  resourceId: string;
  resourceType: string;
}

export const CourseMeta = ({
  className,
  course,
  curriculumList,
  isUserLoggedIn,
  lessons,
  path,
  registrationUrl,
  resourceId,
  resourceType,
  ...props
}: ICourseMeta) => {
  if (!course) {
    return null;
  }

  const { title } = course;
  const {
    courseProgress,
    level,
    ctaLink,
    ctaText,
  } = getCourseMetaContent({ curriculumList, lessons, path, course, isUserLoggedIn });

  return (
    <div className={generateClassName([className, styles.courseMeta])} {...props}>
      <div className={styles.courseMetaHeader}>
        <Icon iconName="Academy" size={2.4} />
      </div>
      <div className={styles.courseMetaBody}>
        <div className={'bold'}>{title}</div>
        {courseProgress > 0 && <div>{`${Math.min(Math.round(courseProgress * 100), 100)}% complete`}</div>}
        <div className={styles.courseMetaItems}>
          {level && <div className={styles.courseMetaItem}><Icon iconName="CheckMark" />{level}</div>}
          {course.estimatedCompletionTimeMinutes && <div className={styles.courseMetaItem}><Icon
            iconName="TimeDelay" />{course.estimatedCompletionTimeMinutes} min</div>}
        </div>
        <RegisterButton
          href={ctaLink}
          isUserLoggedIn={isUserLoggedIn}
          registrationUrl={registrationUrl}
          resourceId={resourceId}
          resourceType={resourceType}
          resourceName={title}
          sendRegistrationEvent={courseProgress === 0}
        >
          {ctaText}
        </RegisterButton>
      </div>
    </div>
  );
};

type TCourseMetaWithData = Omit<ICourseMeta, 'curriculumList' | 'isUserLoggedIn'>;

export const CourseMetaWithData = ({ course, ...props }: TCourseMetaWithData) => {
  const curriculumList = useGetCurriculumList(course.thirdPartyId);
  const loggedIn = useStore($loggedIn);

  return <CourseMeta
    course={course}
    curriculumList={curriculumList}
    isUserLoggedIn={loggedIn}
    {...props}
  />;
};
